/**
 * FP-038, the routes out that are not the sheet's own: browser Back, and leaving the
 * page (reload, close the tab, type a URL).
 *
 * `busy` on a sheet holds its Cancel, X, Escape and outside click (lib/saveGate). Back
 * is still a way out: it unmounts the page, the request carries on, and the write
 * lands with nobody watching. On Goals that includes "Create a new bucket for this
 * goal", which also marks the whole split user-owned and drops the suggestion, so a
 * change the user walked away from alters what Forecast offers them later.
 *
 * While any sheet is busy:
 * - one guard history entry sits on top of the current one (same URL), and Back is
 *   answered by putting it back, so the page stays where it is;
 * - leaving the page asks first (`beforeunload`).
 * When the last busy sheet settles, the guard is popped, but only if it is still on
 * top: a save that navigated on success (delete account goes to "/") must not be
 * undone by the cleanup.
 *
 * The app's `<BrowserRouter>` is not a data router, so react-router's `useBlocker` is
 * not available. A same-URL entry is safe with it: popping back to the entry beneath
 * the guard changes nothing it renders.
 */

const GUARD = "__fp038SaveInFlight";

export type HoldWindow = Pick<Window, "addEventListener" | "removeEventListener"> & {
  history: Pick<History, "pushState" | "back" | "state">;
  location: Pick<Location, "href">;
};

function isGuard(state: unknown): boolean {
  return typeof state === "object" && state !== null && GUARD in state;
}

/** Reference-counted, so nested busy sheets (a confirm over its dialog) hold once. */
export function createNavigationHold(win: HoldWindow) {
  let holders = 0;

  function pushGuard() {
    const state = win.history.state;
    win.history.pushState(
      { ...(typeof state === "object" && state !== null ? state : {}), [GUARD]: true },
      "",
      win.location.href,
    );
  }

  function onPopState() {
    pushGuard();
  }

  function onBeforeUnload(event: BeforeUnloadEvent) {
    event.preventDefault();
    // Older Chromium and Safari still need a value here to show the prompt.
    event.returnValue = "";
  }

  return {
    acquire() {
      holders += 1;
      if (holders > 1) return;
      pushGuard();
      win.addEventListener("popstate", onPopState);
      win.addEventListener("beforeunload", onBeforeUnload);
    },
    release() {
      if (holders === 0) return;
      holders -= 1;
      if (holders > 0) return;
      win.removeEventListener("popstate", onPopState);
      win.removeEventListener("beforeunload", onBeforeUnload);
      if (isGuard(win.history.state)) win.history.back();
    },
    get holders() {
      return holders;
    },
  };
}

let shared: ReturnType<typeof createNavigationHold> | null = null;

/** The app's one hold, on the real window. `null` where there is no window (SSR). */
export function navigationHold() {
  if (typeof window === "undefined") return null;
  shared ??= createNavigationHold(window);
  return shared;
}
